import { StateStatus } from "shared/enums/StateStatus";
import MeetupProgramStatus from "shared/enums/MeetupProgramStatus";
import BaseProgram from "./base";
import SelectionType from "shared/enums/SelectionType";
import I18n from "shared/types/I18n";
import { Copies } from "shared/hooks/useCopies";
import { StaticDataObject } from "context/staticData/staticData";
import { Column } from "react-table";
import { ApiParticipantForSelectionForAdminResponse } from "shared/types/ApiParticipantForSelectionForAdminResponse";
import CellEllipsis from "shared/core/Table/components/cellEllipsis";
import SelectableType from "shared/enums/SelectableType";
import { ProgramParticipantTag } from "shared/enums/ProgramParticipantTag";

export default class MeetupProgram extends BaseProgram {
  canShowNoShowFilter(): boolean {
    if (this.programDetails) {
      return !this.checkProgramState(MeetupProgramStatus.IN_PROGRESS, StateStatus.NOT_YET_TIME);
    }
    return false;
  }
  canAddSponsorship(): boolean {
    if (this.programDetails) {
      return MeetupProgramStatus.isBetweenEnrollmentAndBeforeMeetingMatching(
        this.programDetails.programStatus as MeetupProgramStatus
      );
    }
    return false;
  }
  canAdminMarkAsNoShow(): boolean {
    if (this.programDetails) {
      return this.isProgramStateInBetween({
        firstState: MeetupProgramStatus.IN_PROGRESS,
        secondState: MeetupProgramStatus.RECONCILIATION
      });
    }
    return false;
  }
  canShowAdditionalMeetingMatching(): boolean {
    if (this.programDetails) {
      const hasMeetingMatchingElapsed = this.checkProgramState(
        MeetupProgramStatus.MEETING_MATCHING,
        StateStatus.ELAPSED
      );
      return hasMeetingMatchingElapsed;
    }
    return false;
  }

  canConvertToEmailIntroduction(): boolean {
    if (this.programDetails && this.programDetails.emailIntroductionEnabled) {
      const isBeforeInviteSent = this.checkProgramState(MeetupProgramStatus.INVITES_SENT, StateStatus.NOT_YET_TIME);
      return isBeforeInviteSent ? false : true;
    }
    return false;
  }

  getSwitcherConfigInSelectionRound2(): {
    isAvailable: boolean;
    defaultOption: SelectionType | null;
    screenName: keyof I18n;
  } {
    return {
      isAvailable: false,
      defaultOption: SelectionType.OPT_IN,
      screenName: "selection.round.3"
    };
  }
  getTableColumnsInListForSelectionRoundOne(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectableType: SelectableType
  ): Column<ApiParticipantForSelectionForAdminResponse>[] {
    return [
      ...this.getCommonColumnsForSelectionRounds(copies, staticDataObj, selectableType),
      {
        id: "general-selections-count",
        Header: copies.get("general.selection.count"),
        accessor: "requestCount",
        sortType: "basic",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "general-surplus-percent",
        Header: copies.get("general.surplus.percent"),
        sortType: "basic",
        accessor: d => {
          return d.optedInTimeSlotCount
            ? Math.floor((((d.requestCount || 0) - d.optedInTimeSlotCount) / d.optedInTimeSlotCount) * 100)
            : null;
        },
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "meetings-count",
        Header: copies.get("meetings.available"),
        sortType: "basic",
        accessor: d => {
          return d.unlimitedMeetingCap ? d.optedInTimeSlotCount : d.meetingCap;
        },
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "hosted-selection-count",
        Header: copies.get("hosted.selection"),
        sortType: "basic",
        accessor: "paidMeetingRequestCount",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "hosted-surplus",
        Header: copies.get("hosted.surplus.percent"),
        sortType: "basic",
        accessor: d => {
          if (
            d.applicableTag === ProgramParticipantTag.PURE_BUYER ||
            d.applicableTag === ProgramParticipantTag.SOCIAL_BUYER ||
            d.applicableTag === ProgramParticipantTag.SOCIALIZER
          ) {
            return null;
          }
          if (d.unlimitedMeetingCap) {
            return d.optedInTimeSlotCount
              ? Math.floor(((d.paidMeetingRequestCount - d.optedInTimeSlotCount) / d.optedInTimeSlotCount) * 100)
              : null;
          } else {
            return d.meetingCap ? Math.floor(((d.paidMeetingRequestCount - d.meetingCap) / d.meetingCap) * 100) : null;
          }
        },
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "status",
        Header: copies.get("status"),
        accessor: d => staticDataObj.activityStatus?.[d.selectionStatus],
        width: 180,
        Cell: CellEllipsis
      }
    ];
  }
  getTableColumnsInListForSelectionRoundTwo(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectionType: SelectionType | null,
    selectableType: SelectableType
  ): Column<ApiParticipantForSelectionForAdminResponse>[] {
    return [
      ...this.getCommonColumnsForSelectionRounds(copies, staticDataObj, selectableType),
      {
        id: "general-mutual-match",
        sortType: "basic",
        Header: copies.get("general.mutual.match"),
        accessor: "generalMutualMatchCount",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "selections-count",
        sortType: "basic",
        Header: copies.get("open.general.requests"),
        accessor: "generalInboundRequestCount",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "mutual-match-hosted-count",
        sortType: "basic",
        Header: copies.get("hosted.mutual.match"),
        accessor: "paidMeetingMutualMatchCount",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "open-hosted-count",
        sortType: "basic",
        Header: copies.get("open.hosted.requests"),
        accessor: "paidMeetingInboundRequestCount",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "status",
        Header: copies.get("status"),
        accessor: d => staticDataObj.activityStatus?.[d.selectionStatus],
        width: 180,
        Cell: CellEllipsis
      }
    ];
  }
  canManageMeetingCap(): boolean {
    return true;
  }
  canViewMeetingPurchases(): boolean {
    return true;
  }
  canRemoveOrgFromProgram(): boolean {
    return false;
  }
  canSendNotification(): boolean {
    return this.checkProgramState(MeetupProgramStatus.CONFIGURATION, StateStatus.ELAPSED);
  }
}
